import { ReactNode } from 'react';

import {
  Box,
  Button,
  Flex,
  Group,
  HStack,
  Icon,
  IconButton,
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
  Separator,
  SingleItemSelect,
  Spacer,
  Text,
} from '@oaknorth/component-core-v2';
import { T, useTranslation } from '@oaknorth/util-intl';

import { DEFAULT_PAGE_SIZE, SMALL_PAGE_SIZE } from '../../constants';
import { TableInstance } from '../../types';

interface TablePaginationProps<T extends object> {
  table: TableInstance<T>;

  /**
   * Optionally hide the pagination automatically if there is only one page to show
   */
  hideIfSinglePage?: boolean;

  /**
   * Customise the rendering of the result count.
   * By default, renders "Showing 1-25 of 50"
   * Can customise this to "Showing 1-25 of 50 borrowers"
   *
   * @param resultRange
   * @param totalRows
   * @returns
   */
  renderResultCount?: (firstResultNumber: number, lastResultNumber: number, totalRowCount: number) => ReactNode;
}

const defaultRenderResultCount = (firstResultNumber: number, lastResultNumber: number, totalRowCount: number) =>
  T('table.pagination.resultCount', { firstResultNumber, lastResultNumber, totalRowCount });

const PAGE_SIZE_OPTIONS = [SMALL_PAGE_SIZE, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE * 2, DEFAULT_PAGE_SIZE * 4];

const NUM_VISIBLE_PAGE_BUTTONS = 4;

const ELLIPSES = <Text>...</Text>;
const PAGINATION_BAR_DIVIDER = <Separator orientation="vertical" height={8} borderColor="gray.100" marginX={2} />;

const paginationButtonStyles = {
  color: 'gray.600',
  paddingX: 2,
  minWidth: 5,
  border: 'none',
  _hover: { color: 'blue.300', _disabled: { color: 'on-navy.200' } },
  _active: { color: 'blue.500', background: 'none' },
};

const paginationIconButtonStyles = {
  ...paginationButtonStyles,
  paddingX: 'none',
  color: 'blue.500',
};

// Return array of page indices (integers) given a range (start: inclusive, end: exclusive).
function getPagesInRange(start: number, end: number) {
  const pageIndices: number[] = [];
  for (let i = start; i < end; i++) {
    pageIndices.push(i);
  }
  return pageIndices;
}

/**
 * Determines the range of page number buttons to show in the pagination bar.
 * If we're in the last 4 pages, show the last 4 pages (Or all the pages if page count <= 4)
 * Otherwise, show current page, next page, and previous 2 pages.
 * Second statement also handles case when we are in the first 3 pages if page count > 4
 */
export function getVisiblePages({ pageIndex, pageCount }: { pageIndex: number; pageCount: number }): number[] {
  if (pageIndex > pageCount - NUM_VISIBLE_PAGE_BUTTONS) {
    return getPagesInRange(Math.max(pageCount - NUM_VISIBLE_PAGE_BUTTONS, 0), Math.max(pageCount, 1));
  }
  return getPagesInRange(Math.max(0, pageIndex - 2), Math.max(pageIndex + 2, NUM_VISIBLE_PAGE_BUTTONS));
}

const PageNumberSelector = ({
  activePageIndex,
  pageCount,
  onSelectPage,
}: {
  activePageIndex: number;
  pageCount: number;
  onSelectPage: (pageIndex: number) => void;
}) => {
  const visiblePages = getVisiblePages({ pageCount, pageIndex: activePageIndex });
  /**
   * Include a link to the last page if the visible page number buttons don't include the last page
   * (i.e. if we are more than 4 pages away from the last)
   */
  const showAdditionalLastPageLink = pageCount > 0 && !visiblePages.includes(pageCount - 1);
  return (
    <>
      {visiblePages.map((pageIndex) => {
        const isActive = activePageIndex === pageIndex;
        return (
          <Button
            key={pageIndex}
            onClick={() => {
              if (isActive) {
                return;
              }
              onSelectPage(pageIndex);
            }}
            {...paginationButtonStyles}
            color={isActive ? 'blue.fg' : 'fg.muted'}
            {...(isActive ? { 'data-active': true } : {})}
          >
            {pageIndex + 1}
          </Button>
        );
      })}
      {showAdditionalLastPageLink ? (
        <>
          {ELLIPSES}
          <Button
            onClick={() => {
              onSelectPage(pageCount - 1);
            }}
            {...paginationButtonStyles}
          >
            {pageCount}
          </Button>
        </>
      ) : null}
    </>
  );
};

export const TablePagination = <T extends object = {}>({
  table,
  hideIfSinglePage,
  renderResultCount = defaultRenderResultCount,
}: TablePaginationProps<T>) => {
  const { t } = useTranslation();
  const { pageIndex, pageSize } = table.getState().pagination;
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();
  const pageCount = table.getPageCount();
  const totalRows = table.rowCount;

  const handleFirst = () => {
    table.setPageIndex(0);
  };

  const handleLast = () => {
    table.setPageIndex(pageCount - 1);
  };

  const hasSinglePage = (totalRows ?? 0) < pageSize && pageCount <= 1;
  if (hasSinglePage && hideIfSinglePage) {
    return null;
  }

  const firstResult = pageIndex * pageSize + 1;
  const lastResult = Math.min(firstResult + pageSize - 1, totalRows ?? Number.POSITIVE_INFINITY);

  return (
    <HStack
      data-testid="table-pagination"
      paddingX={6}
      paddingY={3}
      align="center"
      gap={2}
      borderTop="1px"
      borderColor="gray.100"
    >
      <Flex align="center">
        <Box whiteSpace="nowrap" textStyle="tableHeader" fontSize="sm">
          {totalRows ? renderResultCount(firstResult, lastResult, totalRows) : null}
        </Box>
      </Flex>
      <Spacer />
      <Group attached borderRadius="full" border="1px solid" borderColor="gray.400" paddingX={4} alignItems="center">
        <IconButton
          data-testid="pagination-first"
          disabled={!canPreviousPage}
          onClick={handleFirst}
          aria-label={t`table.pagination.firstPage`}
          {...paginationIconButtonStyles}
        >
          <Icon as={MdFirstPage} boxSize={6} />
        </IconButton>
        <IconButton
          data-testid="pagination-prev"
          disabled={!canPreviousPage}
          onClick={table.previousPage}
          aria-label={t`table.pagination.previousPage`}
          {...paginationIconButtonStyles}
        >
          <Icon as={MdChevronLeft} boxSize={6} />
        </IconButton>
        {PAGINATION_BAR_DIVIDER}
        <PageNumberSelector pageCount={pageCount} activePageIndex={pageIndex} onSelectPage={table.setPageIndex} />
        {PAGINATION_BAR_DIVIDER}
        <IconButton
          data-testid="pagination-next"
          disabled={!canNextPage}
          onClick={table.nextPage}
          aria-label={t`table.pagination.nextPage`}
          {...paginationIconButtonStyles}
        >
          <Icon as={MdChevronRight} boxSize={6} />
        </IconButton>
        <IconButton
          data-testid="pagination-last"
          disabled={!canNextPage}
          onClick={handleLast}
          aria-label={t`table.pagination.lastPage`}
          {...paginationIconButtonStyles}
        >
          <Icon as={MdLastPage} boxSize={6} />
        </IconButton>
      </Group>
      <Box>
        <SingleItemSelect<number>
          inputId="pageSize"
          onChange={(option) => option && table.setPageSize(option)}
          options={PAGE_SIZE_OPTIONS}
          getOptionLabel={(pageSize) => T('table.pagination.pageSize.items', { count: pageSize })}
          getOptionValue={(pageSize) => pageSize.toString()}
          value={pageSize}
          menuPosition="fixed"
        />
      </Box>
    </HStack>
  );
};
